// Loads videos, brands and studios with 3 queries in total and joins them in
// memory. Replaces the previous 1 + 2×N reads per page view.

import {
  collection,
  getDocs,
  query,
  QueryConstraint,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { Entity, Video, VideoThumbnail } from "../types/cms";

export interface Catalog {
  videos: VideoThumbnail[];
  companies: Entity[];
  agencies: Entity[];
}

type Scope = "public" | "admin";

const cache: Partial<Record<Scope, Promise<Catalog>>> = {};

export function normalizeEntity(id: string, data: any): Entity {
  const logo = data?.logoURL;
  return {
    ...data,
    id,
    name: data?.name ?? "",
    websiteURL: data?.websiteURL ?? "",
    logoURL: Array.isArray(logo) ? logo.filter(Boolean) : logo ? [logo] : [],
  } as Entity;
}

export function unknownEntity(label: string): Entity {
  return { id: "", name: label, websiteURL: "", logoURL: [], createdAt: Timestamp.now() };
}

export function firstLogo(entity?: Pick<Entity, "logoURL"> | null): string | undefined {
  return entity?.logoURL?.[0];
}

export function publishedVideosQuery(...constraints: QueryConstraint[]) {
  return query(collection(db, "videos"), where("status", "==", "published"), ...constraints);
}

export function joinVideos(
  videos: Video[],
  companies: Entity[],
  agencies: Entity[],
): VideoThumbnail[] {
  const byId = new Map<string, Entity>();
  // Legacy data sometimes stores a studio as "Video For" and vice versa:
  // resolve ids against both collections, preferring the expected one.
  agencies.forEach((a) => byId.set(`a:${a.id}`, a));
  companies.forEach((c) => byId.set(`c:${c.id}`, c));
  const resolve = (id: string | undefined, prefer: "a" | "c") =>
    id ? byId.get(`${prefer}:${id}`) ?? byId.get(`${prefer === "a" ? "c" : "a"}:${id}`) : undefined;

  return videos.map((video) => ({
    ...video,
    styleframesURLs: video.styleframesURLs ?? [],
    industries: video.industries ?? [],
    formats: video.formats ?? [],
    styles: video.styles ?? [],
    tags: video.tags ?? [],
    videoFor: resolve(video.enterpriseId, "c") ?? unknownEntity("Unknown brand"),
    producedBy: resolve(video.agencyId, "a") ?? unknownEntity("Unknown studio"),
  }));
}

async function fetchCatalog(scope: Scope): Promise<Catalog> {
  const videosQuery =
    scope === "admin" ? collection(db, "videos") : publishedVideosQuery();
  const [videosSnap, companiesSnap, agenciesSnap] = await Promise.all([
    getDocs(videosQuery),
    getDocs(collection(db, "companies")),
    getDocs(collection(db, "agencies")),
  ]);
  const companies = companiesSnap.docs.map((d) => normalizeEntity(d.id, d.data()));
  const agencies = agenciesSnap.docs.map((d) => normalizeEntity(d.id, d.data()));
  const videos = videosSnap.docs.map((d) => ({ ...(d.data() as Video), id: d.id }));
  const joined = joinVideos(videos, companies, agencies).sort(
    (a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0),
  );
  return { videos: joined, companies, agencies };
}

export function loadCatalog(scope: Scope = "public", force = false): Promise<Catalog> {
  if (!cache[scope] || force) {
    const pending = fetchCatalog(scope);
    cache[scope] = pending;
    pending.catch(() => {
      if (cache[scope] === pending) delete cache[scope];
    });
  }
  return cache[scope]!;
}

/** Call after any write so the next read reflects it. */
export function invalidateCatalog() {
  delete cache.public;
  delete cache.admin;
}
